import { memo, useMemo, useRef } from 'react';

import { GetMainRepresentationByArticleIdLayerLoadMode as LayerLoadMode, type PrefabModel } from 'api/models';
import { useGetMainRepresentationByArticleId } from 'api/queries/articles/articles';
import { getGLTFUrls } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { GltfAsset } from 'app/modules/wallforming/components/scenes/objects/helpers/types/gltfAsset';
import { QuickPanelReplacementPreview } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/QuickPanelReplacementPreview';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface RepresentationLoaderProps {
    articleId: string;
    articleNumber?: string;
    isInteractive: boolean;
    children: (props: {
        articleId: string;
        isInteractive: boolean;
        articleRepresentation: PrefabModel;
        gltfAssets: GltfAsset[];
    }) => React.ReactNode;
}

export const RepresentationLoader = memo(
    ({ articleId, articleNumber, isInteractive, children }: RepresentationLoaderProps) => {
        // MIND: this component renders multiple times

        const articleNumberRef = useRef<string>(
            articleNumber ?? usePlacementStore.getState().articles[articleId]?.articleNumber ?? ''
        );

        const queryKey = useMemo(() => ['representation', articleId, LayerLoadMode.LogicLayers], [articleId]);

        const { data: articleRepresentation, isLoading: isLoadingArticleRepresentation } =
            useGetMainRepresentationByArticleId(
                articleId,
                { layerLoadMode: LayerLoadMode.LogicLayers, includePrefabComponents: false },
                { query: { queryKey, staleTime: 24 * 60 * 60 * 1000 } }
            );

        const gltfAssets: GltfAsset[] = useMemo(() => {
            if (!articleRepresentation?.data) {
                return [];
            }

            const assets = getGLTFUrls(articleNumberRef.current, articleRepresentation.data);
            return assets;
        }, [articleRepresentation?.data]);

        if (isLoadingArticleRepresentation) {
            return <QuickPanelReplacementPreview articleId={articleId} />;
        }

        if (!articleRepresentation?.data || gltfAssets.length === 0) {
            throw new Error('errorLoadingArticle');
        }

        return (
            <>
                {children?.({
                    articleId,
                    articleRepresentation: articleRepresentation?.data,
                    gltfAssets,
                    isInteractive
                })}
            </>
        );
    }
);

RepresentationLoader.displayName = 'RepresentationLoader';
